import { useState, useEffect } from "react";
import apiClient from '@shared/services/apiClient';
import { useProtectedFetchGate } from "@shared/hooks/useProtectedFetchGate";
import logger from "@shared/utils/logger";

interface UseFetchDataOptions {
  requiresAuth?: boolean;
}

const useFetchData = <T>(endpoint: string, options: UseFetchDataOptions = {}) => {
  const requiresAuth = options.requiresAuth ?? true;
  const { shouldBlockProtectedFetch, authLoading } = useProtectedFetchGate();
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (requiresAuth && shouldBlockProtectedFetch) {
      setLoading(authLoading);
      setError(null);
      setData([]);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Use apiClient which automatically includes credentials
        const response = await apiClient.get(endpoint);

        setData(response.data?.data || response.data || []);
      } catch (err: any) {
        logger.error("API request error:", err);
        setError(
          err.response?.data?.message || err.message || "Failed to fetch data",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [endpoint, requiresAuth, shouldBlockProtectedFetch, authLoading]);

  return { data, loading, error };
};

export default useFetchData;
